import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, ArrowDownRight, ShieldCheck } from "lucide-react";
import { BRAND, WA_DEFAULT, HERO_IMAGE, waLink } from "@/data/site";
import { scrollToId } from "@/lib/lenis";

const LINES = [
  { text: "Seu visto internacional", accent: false },
  { text: "com excelência e", accent: false },
  { text: "precisão diplomática.", accent: true },
];

const STATS = [
  { value: "98.7%", label: "taxa de aprovação", testid: "hero-stat-approval-rate" },
  { value: "+4.800", label: "vistos emitidos", testid: "hero-stat-visas-issued" },
  { value: "100%", label: "suporte individual", testid: "hero-stat-support" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="inicio"
      ref={ref}
      className="grain relative flex min-h-screen items-center overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="pointer-events-none absolute inset-0">
        <img
          src={HERO_IMAGE}
          alt="Passaportes e bilhetes internacionais"
          className="h-[120%] w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-950/70 via-navy-950/45 to-navy-950" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_30%,rgba(212,175,55,0.14),transparent_55%)]" />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-24 pt-36 sm:px-8 lg:pt-40"
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.7 }}
          className="mb-8 inline-flex items-center gap-2 rounded-full border border-gold-400/30 bg-navy-900/60 px-4 py-1.5 backdrop-blur-md"
        >
          <ShieldCheck className="h-3.5 w-3.5 text-gold-400" />
          <span className="font-mono-tech text-[10px] tracking-[0.25em] text-gold-300 sm:text-xs">
            CNPJ {BRAND.cnpj} • ASSESSORIA HOMOLOGADA
          </span>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="font-serif-display max-w-4xl text-4xl font-light leading-[1.08] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl xl:text-7xl"
        >
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{
                  delay: 0.25 + i * 0.14,
                  duration: 1,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className="block"
              >
                {line.accent ? (
                  <>
                    precisão{" "}
                    <em className="gold-text font-medium italic">
                      diplomática.
                    </em>
                  </>
                ) : (
                  line.text
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.8 }}
          className="mt-7 max-w-xl text-base font-light leading-relaxed text-slate-300 sm:text-lg"
        >
          Assessoria consular especializada para vistos americano, canadense,
          mexicano, chinês, japonês, australiano, cidadania italiana (via
          consular e judicial) e emissão de passaportes — preparação rigorosa
          para maximizar suas chances de aprovação.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            data-testid="hero-whatsapp-button"
            href={WA_DEFAULT}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3 rounded-full bg-gold-400 px-7 py-3.5 text-sm font-semibold tracking-wide text-navy-950 transition-colors duration-300 hover:bg-gold-200"
          >
            <MessageCircle className="h-4 w-4 transition-transform duration-300 group-hover:-rotate-12" />
            Falar no WhatsApp — {BRAND.phone}
          </a>
          <button
            data-testid="hero-analyze-profile-button"
            onClick={() => scrollToId("#analise")}
            className="group flex items-center gap-2 rounded-full border border-slate-500/50 px-7 py-3.5 text-sm font-medium text-slate-200 transition-colors duration-300 hover:border-gold-400/60 hover:text-gold-200"
          >
            Iniciar análise de perfil
            <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:translate-y-0.5" />
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 1 }}
          className="mt-16 grid max-w-2xl grid-cols-3 divide-x divide-gold-400/15 border-y border-gold-400/15"
        >
          {STATS.map((s) => (
            <div key={s.testid} data-testid={s.testid} className="px-4 py-5 first:pl-0">
              <p className="font-serif-display text-2xl text-gold-300 sm:text-4xl">
                {s.value}
              </p>
              <p className="mt-1 font-mono-tech text-[9px] uppercase tracking-[0.2em] text-slate-400 sm:text-[10px]">
                {s.label}
              </p>
            </div>
          ))}
        </motion.div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.35, duration: 1 }}
          className="mt-4 max-w-2xl text-[11px] font-light leading-relaxed text-slate-500"
        >
          *Índices históricos da carteira de clientes atendidos. A concessão de
          vistos é decisão exclusiva e soberana dos consulados e embaixadas;
          nenhuma assessoria pode garantir aprovação.
        </motion.p>
      </motion.div>
    </section>
  );
};

export { waLink };
